import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, tap } from 'rxjs'
import type { ObjectsService, ObjectUpdatedEvent } from './ObjectsService'
import type { CollectionViewsService } from './CollectionViewsService'
import type { CollectionsTreeService } from './CollectionsTreeService'
import { ObjectFilterService } from './ObjectFilterService'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable'
import type { PropertyType } from '@/object-types/gql/PropertyType.ts'
import type { CollectionModel, ObjectModel } from '@/core/gql/gql-models-override.ts'
import type { PropertyOptionModel } from '@/generated/graphql.ts'
import { log } from '@/common/errors/log-utils.ts'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'

export interface CollectionObjectsGroup {
  groupByOption: PropertyOptionModel | undefined
  objects: ObjectModel[]
}

export interface CollectionTabObjects {
  isGrouped: boolean
  groups: CollectionObjectsGroup[]
}

export const groupableTypes = new Set<PropertyType>(['select-1'])

export class CollectionObjectsService {
  private readonly filterService = new ObjectFilterService()
  private readonly _collectionIdToObjects = new Map<
    string,
    BehaviorSubject<CollectionTabObjects | undefined>
  >()

  constructor(
    private readonly objectsService: ObjectsService,
    private readonly collectionViewsService: CollectionViewsService,
    private readonly collectionsTreeService: CollectionsTreeService,
    private readonly objectTypesService: ObjectTypesService,
  ) {
    const lsState = this.collectionViewsService.localStorageState
    if (lsState) {
      this.getActiveTabObjects$(lsState.lastOpenedCollectionId, lsState.lastOpenedCollectionTypeId)
    }
    this.objectsService.objectUpdated$.subscribe(this.onObjectUpdated.bind(this))
  }

  public getActiveTabObjects$(
    collectionId: string,
    typeId: number,
  ): IBehaviorObservable<CollectionTabObjects | undefined> {
    const existing = this._collectionIdToObjects.get(collectionId)
    if (existing) return existing

    const subj = new BehaviorSubject<CollectionTabObjects | undefined>(undefined)
    this._collectionIdToObjects.set(collectionId, subj)

    const groupByColumnId$ = this.collectionViewsService.getViewSettings$(collectionId).pipe(
      map((vs) => vs?.groupByColumnId),
      distinctUntilChanged(),
    )

    combineLatest([
      this.objectsService.objectsOfType$(typeId),
      this.collectionViewsService.getTabs$(collectionId),
      this.collectionViewsService.getActiveTabId$(collectionId),
      groupByColumnId$,
    ])
      .pipe(
        // needed bc behavior observables emit on creation, so combineLatest "wait until all emitted once" does not work
        debounceTime(0),
        map(([objects, tabs, activeTabId, groupByColumnId]) => {
          if (!objects || !tabs) return undefined
          const tab = tabs.find((t) => t.id === activeTabId)
          if (!tab) return undefined

          this.filterService.filterTree = tab.filterTree
          const groupByPropId = groupByColumnId ?? undefined

          // log(`filtering objects`, { state: { groupByColumnId, tab, objects } })
          return this.groupAndFilter(objects, groupByPropId, typeId)
        }),
        tap((result) => {
          subj.next(result)
        }),
      )
      .subscribe()

    return subj
  }

  private onObjectUpdated({ typeId, prev, updated }: ObjectUpdatedEvent): void {
    for (const [collectionId, subj] of this._collectionIdToObjects) {
      const current = subj.value
      if (!current) continue

      const node = this.collectionsTreeService.collectionsTree?.idToNode.get(collectionId) as
        | CollectionModel
        | undefined
      if (!node || node.typeId !== typeId) continue

      const activeTabId = this.collectionViewsService.getActiveTabId$(collectionId).value
      const tab = this.collectionViewsService.getTabs$(collectionId).value?.find(
        (t) => t.id === activeTabId,
      )
      if (!tab) continue
      this.filterService.filterTree = tab.filterTree

      const newGroups = this.applyObjectUpdate(current, collectionId, prev, updated)
      if (newGroups !== undefined) subj.next({ ...current, groups: newGroups })
    }
  }

  private getOption(
    groupByPropId: number,
    rawValue: number,
    typeId: number,
  ): PropertyOptionModel | undefined {
    const propDef = this.objectTypesService
      .getTypeFromSelectedSpace(typeId)
      ?.propertyDefinitions.find((d) => d.id === groupByPropId)
    return propDef?.options?.find((o) => o.id === rawValue)
  }

  private groupAndFilter(
    objects: ObjectModel[],
    groupByPropId: number | undefined,
    typeId: number,
  ): CollectionTabObjects {
    const buckets = new Map<number | undefined, ObjectModel[]>()

    for (const obj of objects) {
      if (!this.filterService.matches(obj)) continue

      const raw =
        groupByPropId !== undefined
          ? (obj.propertyValues.get(groupByPropId) as number | null | undefined)
          : undefined
      const groupKey = raw ?? undefined

      const bucket = buckets.get(groupKey)
      if (bucket) {
        bucket.push(obj)
      } else {
        buckets.set(groupKey, [obj])
      }
    }

    return {
      isGrouped: groupByPropId !== undefined,
      groups: Array.from(buckets.entries()).map(([rawKey, objects]) => ({
        groupByOption:
          groupByPropId !== undefined && rawKey !== undefined
            ? this.getOption(groupByPropId, rawKey, typeId)
            : undefined,
        objects,
      })),
    }
  }

  // Returns new groups array if a change was made, undefined if nothing to do.
  private applyObjectUpdate(
    current: CollectionTabObjects,
    collectionId: string,
    prev: ObjectModel | undefined,
    updated: ObjectModel | undefined,
  ): CollectionObjectsGroup[] | undefined {
    const groupByPropId =
      this.collectionViewsService.getViewSettings$(collectionId).value?.groupByColumnId ?? undefined

    const prevId = prev?.id

    let groupIdx = -1
    let objIdx = -1
    if (prevId !== undefined) {
      for (let g = 0; g < current.groups.length; g++) {
        const i = current.groups[g].objects.findIndex((o) => o.id === prevId)
        if (i !== -1) {
          groupIdx = g
          objIdx = i
          break
        }
      }
    }
    const updatedPasses = updated !== undefined && this.filterService.matches(updated)

    if (groupIdx === -1 && !updatedPasses) return undefined

    const groups = [...current.groups]

    if (groupIdx !== -1) {
      groups[groupIdx] = {
        ...groups[groupIdx],
        objects: groups[groupIdx].objects.filter((_, i) => i !== objIdx),
      }
    }

    if (updatedPasses) {
      const rawGroupVal =
        groupByPropId !== undefined
          ? (updated.propertyValues.get(groupByPropId) as number | null)
          : undefined
      const targetIdx = groups.findIndex((g) =>
        rawGroupVal != null ? g.groupByOption?.id === rawGroupVal : g.groupByOption === undefined,
      )
      if (targetIdx !== -1) {
        const insertAt =
          groupIdx !== -1 && targetIdx === groupIdx ? objIdx : groups[targetIdx].objects.length
        groups[targetIdx] = {
          ...groups[targetIdx],
          objects: [
            ...groups[targetIdx].objects.slice(0, insertAt),
            updated,
            ...groups[targetIdx].objects.slice(insertAt),
          ],
        }
      } else {
        const groupByOption =
          rawGroupVal != null && groupByPropId !== undefined
            ? this.getOption(groupByPropId, rawGroupVal, updated.typeId)
            : undefined
        groups.push({ groupByOption, objects: [updated] })
      }
    }

    return groups
  }
}
